import React, { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

// eslint-disable-next-line security/detect-unsafe-regex
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

function formatDate(dateStr, timezone) {
  if (!dateStr) { return null; }
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    timeZone: timezone || 'America/Denver',
  });
}

function formatTime(dateStr, timezone) {
  if (!dateStr) { return null; }
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric', minute: '2-digit',
    timeZone: timezone || 'America/Denver',
  });
}

export default function EventRSVPIntentView({ intentCode, hubspotContactId = null }) {
  const [state, setState] = useState('loading');
  const [config, setConfig] = useState(null);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchConfig() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v1/marketing/intent-rsvp-config/${intentCode}`);
        if (cancelled) { return; }

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          if (res.status === 410 && data.error === 'INTENT_EXPIRED') {
            setState('expired');
          } else if (res.status === 410 && data.error === 'INTENT_EXHAUSTED') {
            setState('exhausted');
          } else if (res.status === 404) {
            setState('not_found');
          } else {
            setState('error');
          }
          return;
        }

        const data = await res.json();
        if (cancelled) { return; }

        if (data.event && data.event.endsAt && new Date(data.event.endsAt) < new Date()) {
          setConfig(data);
          setState('ended');
          return;
        }

        setConfig(data);
        setState('form');
      } catch {
        if (!cancelled) { setState('error'); }
      }
    }
    fetchConfig();
    return () => { cancelled = true; };
  }, [intentCode]);

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setValidationError('Email is required');
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setValidationError('Please enter a valid email address');
      return;
    }

    setValidationError(null);
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/marketing/intent-rsvp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          intentCode,
          email: trimmedEmail,
          firstName: firstName.trim() || undefined,
          consentGiven: true,
          // SCRUM-2231: per-recipient referrer (HubSpot contact objectId) from
          // the ?ref={{contact.hs_object_id}} on the invite link, forwarded
          // through IntentResponsePage. Server resolves attribution via the
          // HubSpot module (SCRUM-2232).
          hubspotContactId: hubspotContactId || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'INTENT_EXPIRED') {
          setState('expired');
        } else if (data.error === 'INTENT_EXHAUSTED') {
          setState('exhausted');
        } else if (data.error === 'EVENT_INVALID') {
          setState('ended');
        } else {
          setState('error');
        }
        setSubmitting(false);
        return;
      }

      const data = await res.json();
      if (data.already_registered) {
        setState('already_registered');
      } else {
        setState('success');
      }
    } catch {
      setState('error');
      setSubmitting(false);
    }
  }

  const event = config?.event;

  if (state === 'loading') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" role="status" aria-label="Loading" />
      </div>
    );
  }

  if (state === 'expired') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This RSVP link has expired</h1>
        <p className="text-gray-600">This registration link is no longer active. Please contact the event organizer for assistance.</p>
      </div>
    );
  }

  if (state === 'exhausted') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This event has reached capacity</h1>
        <p className="text-gray-600">Registration for this event is full. Please contact the event organizer about availability.</p>
      </div>
    );
  }

  if (state === 'ended') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">This event has ended</h1>
        <p className="text-gray-600">
          {event?.name ? `${event.name} has already taken place.` : 'This event has already taken place.'}
          {' '}Keep an eye out for future events!
        </p>
      </div>
    );
  }

  if (state === 'not_found') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Event not found</h1>
        <p className="text-gray-600">We couldn&apos;t find the event you&apos;re looking for.</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-3">Something went wrong</h1>
        <p className="text-gray-600 mb-4">Please try again later.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-2 bg-[#009263] text-white rounded-lg hover:bg-[#007a53] transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="text-[#009263] text-5xl mb-4" aria-hidden="true">&#10003;</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You&apos;re in!</h1>
        <p className="text-gray-600 mb-4">
          You&apos;re confirmed for {event?.name || 'the event'}.
        </p>
        {event && (
          <div className="text-sm text-gray-500 space-y-1">
            {event.startsAt && <p>{formatDate(event.startsAt, event.timezone)} &middot; {formatTime(event.startsAt, event.timezone)}</p>}
            {event.locationName && <p>{event.locationName}</p>}
            {event.locationAddress && <p>{event.locationAddress}</p>}
          </div>
        )}
        <p className="text-sm text-gray-500 mt-4">A confirmation email is on its way.</p>
      </div>
    );
  }

  if (state === 'already_registered') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <div className="text-[#009263] text-5xl mb-4" aria-hidden="true">&#10003;</div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">You&apos;re already registered!</h1>
        <p className="text-gray-600 mb-4">
          You&apos;ve already RSVPd for {event?.name || 'this event'}.
        </p>
        {event && (
          <div className="text-sm text-gray-500 space-y-1">
            {event.startsAt && <p>{formatDate(event.startsAt, event.timezone)} &middot; {formatTime(event.startsAt, event.timezone)}</p>}
            {event.locationName && <p>{event.locationName}</p>}
          </div>
        )}
      </div>
    );
  }

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#009263] focus:border-[#009263]';

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      <h1 className="text-xl font-semibold text-gray-900 mb-2 text-center">
        {event?.name || 'Event RSVP'}
      </h1>

      {event && (
        <div className="mb-6 text-center text-sm text-gray-600 space-y-1">
          {event.startsAt && (
            <p>{formatDate(event.startsAt, event.timezone)} &middot; {formatTime(event.startsAt, event.timezone)}</p>
          )}
          {event.locationName && <p>{event.locationName}</p>}
          {event.locationAddress && <p className="text-gray-500">{event.locationAddress}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {validationError && (
          <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{validationError}</div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            placeholder="you@example.com"
            required
            data-testid="email-input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputClass}
            placeholder="Jane"
            data-testid="firstname-input"
          />
        </div>
        <button
          type="submit"
          disabled={submitting}
          className="w-full min-h-[44px] px-6 py-3 bg-[#009263] text-white font-medium rounded-lg hover:bg-[#007a53] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-button"
        >
          {submitting ? 'Submitting...' : 'RSVP'}
        </button>
      </form>
    </div>
  );
}
